import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiUrl } from '../../services/api';
import { useAuth } from '../../store/AuthContext';
import { 
  Activity, 
  Clock, 
  Loader2, 
  CheckCircle, 
  XCircle, 
  RefreshCw, 
  Brain, 
  ShieldOff, 
  ShieldCheck 
} from 'lucide-react';

const RoverIcon = ({ size = 20, color = "currentColor" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke={color} 
    strokeWidth="1.5" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M4 16h16l-2-6H6l-2 6z" />
    <circle cx="6" cy="18" r="2" />
    <circle cx="12" cy="18" r="2" />
    <circle cx="18" cy="18" r="2" />
    <path d="M9 10L10 6" />
    <rect x="9" y="3" width="5" height="3" rx="1" />
    <path d="M15 10l2-2" />
  </svg>
);

const MissionStatusPage = () => {
  const { token } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const resp = await axios.get(`${getApiUrl()}/setup/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(resp.data.data);
    } catch (err) {
      console.error('Failed to fetch mission status', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [token]);

  const handleForceUpdate = async (name) => {
    try {
      await axios.post(`${getApiUrl()}/setup/intel/force-update`, { name }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchStatus();
      setMessage({ type: 'success', text: `FORCE UPDATE TRIGGERED FOR ${name}` });
    } catch (err) {
      setMessage({ type: 'error', text: 'FAILED TO TRIGGER FORCE UPDATE' });
    }
  };

  const handleUnban = async (name) => {
    try {
      await axios.delete(`${getApiUrl()}/setup/prohibited/${name}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchStatus();
      setMessage({ type: 'success', text: `BRAND ${name} UNBANNED SUCCESSFULLY` });
    } catch (err) {
      setMessage({ type: 'error', text: 'FAILED TO UNBAN BRAND' });
    }
  };

  const getRelativeTime = (date) => {
    if (!date) return 'NEVER';
    const now = new Date();
    const past = new Date(date);
    const diffInMs = now - past;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
      if (diffInHours === 0) return 'Just now';
      return `Hace ${diffInHours}h`;
    }
    return `Hace ${diffInDays}d`;
  };

  if (loading && !status) return <div style={{ color: '#0B3D91', padding: '20px' }}>CONNECTING TO SHIP TELEMETRY...</div>;

  return (
    <div style={{ maxWidth: '1000px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '30px' }}>
        <RoverIcon size={28} color="#E27B58" />
        <h1 style={{ margin: 0, letterSpacing: '2px', textTransform: 'uppercase' }}>Mission Ship Status</h1>
      </div>

      {message && (
        <div style={{
          marginBottom: '20px',
          padding: '12px',
          fontSize: '0.8rem',
          backgroundColor: message.type === 'success' ? 'rgba(0, 184, 148, 0.1)' : 'rgba(214, 48, 49, 0.1)',
          color: message.type === 'success' ? '#00B894' : '#D63031',
          border: `1px solid ${message.type === 'success' ? '#00B894' : '#D63031'}`,
          textAlign: 'center'
        }}>
          {message.text}
        </div>
      )}

      <div style={{ 
        background: 'rgba(5, 7, 10, 0.9)', 
        border: '1px solid #E27B58', 
        padding: '30px',
        borderRadius: '4px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '30px', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <RoverIcon size={20} color="#E27B58" />
            <h2 style={{ margin: 0, fontSize: '0.8rem', letterSpacing: '2px', textTransform: 'uppercase', color: '#E27B58' }}>
              Real-time Fleet Telemetry
            </h2>
          </div>
          <button 
            onClick={fetchStatus}
            className="nasa-button" 
            style={{ fontSize: '0.6rem', padding: '5px 10px', height: 'auto' }}
          >
            <RefreshCw size={12} style={{ marginRight: '5px' }} className={loading ? 'animate-spin' : ''} /> REFRESH TELEMETRY
          </button>
        </div>

        {status ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '15px', marginBottom: '40px' }}>
              <StatusCard icon={Clock} label="PENDING" value={status.summary.pending} color="#888" />
              <StatusCard icon={Loader2} label="SCANNING" value={status.summary.processing} color="#00CEC9" animated={status.summary.processing > 0} />
              <StatusCard icon={CheckCircle} label="DOWNLOADED" value={status.summary.completed} color="#00B894" />
              <StatusCard icon={XCircle} label="FAILED" value={status.summary.failed} color="#D63031" />
            </div>

            {/* GLOBAL INTEL FLEET */}
            <div style={{ marginBottom: '40px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                <Brain size={18} color="#00CEC9" />
                <h3 style={{ fontSize: '0.8rem', color: '#00CEC9', margin: 0, letterSpacing: '1px' }}>GLOBAL INTEL FLEET ARCHIVES</h3>
              </div>
              <div style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #1a1a1a' }}>
                <table style={{ width: '100%', minWidth: '560px', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#05070A' }}>
                    <tr style={{ color: '#888', borderBottom: '1px solid #333', textAlign: 'left' }}>
                      <th style={{ padding: '12px 8px' }}>BRAND NAME</th>
                      <th style={{ padding: '12px 8px' }}>STATUS</th>
                      <th style={{ padding: '12px 8px' }}>RESULTS</th>
                      <th style={{ padding: '12px 8px' }}>LAST SUCCESS</th>
                      <th style={{ padding: '12px 8px' }}>RETRIES</th>
                      <th style={{ padding: '12px 8px' }}>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {status.intelFleet.length > 0 ? status.intelFleet.map(intel => (
                      <tr key={intel._id} style={{ borderBottom: '1px solid #1a1a1a', color: '#DDD' }}>
                        <td style={{ padding: '12px 8px', fontWeight: 'bold' }}>{intel.name}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={{ 
                            padding: '2px 6px', 
                            borderRadius: '2px', 
                            backgroundColor: `${getStatusColor(intel.status)}22`,
                            color: getStatusColor(intel.status),
                            fontSize: '0.6rem'
                          }}>
                            {intel.status.toUpperCase()}
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px' }}>
                          <span style={{ color: intel.resultCount > 0 ? '#00B894' : '#555', fontWeight: 'bold' }}>
                            {intel.resultCount || 0}
                          </span>
                        </td>
                        <td style={{ padding: '12px 8px', color: '#666' }}>
                          {getRelativeTime(intel.lastDownloadSuccess)}
                        </td>
                        <td style={{ padding: '12px 8px' }}>{intel.retryCount}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <button 
                            onClick={() => handleForceUpdate(intel.name)}
                            style={{ background: 'none', border: 'none', color: '#00CEC9', cursor: 'pointer' }}
                            title="Force Re-download"
                          >
                            <RefreshCw size={14} />
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#555' }}>FLEET ARCHIVES EMPTY</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* PROHIBITED BRANDS */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                <ShieldOff size={18} color="#D63031" />
                <h3 style={{ fontSize: '0.8rem', color: '#D63031', margin: 0, letterSpacing: '1px' }}>PROHIBITED BRANDS (BLACKLIST)</h3>
              </div>
              <div style={{ maxHeight: '300px', overflowY: 'auto', overflowX: 'auto', border: '1px solid #1a1a1a' }}>
                <table style={{ width: '100%', minWidth: '420px', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#05070A' }}>
                    <tr style={{ color: '#888', borderBottom: '1px solid #333', textAlign: 'left' }}>
                      <th style={{ padding: '12px 8px' }}>BRAND NAME</th>
                      <th style={{ padding: '12px 8px' }}>REASON</th>
                      <th style={{ padding: '12px 8px' }}>BANNED DATE</th>
                      <th style={{ padding: '12px 8px' }}>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                  {status.prohibitedBrands.length > 0 ? status.prohibitedBrands.map(pb => (
                      <tr key={pb._id} style={{ borderBottom: '1px solid #1a1a1a', color: '#DDD' }}>
                        <td style={{ padding: '12px 8px', fontWeight: 'bold' }}>{pb.name}</td>
                        <td style={{ padding: '12px 8px', opacity: 0.7 }}>{pb.reason}</td>
                        <td style={{ padding: '12px 8px', color: '#666' }}>{new Date(pb.bannedAt).toLocaleString()}</td>
                        <td style={{ padding: '12px 8px' }}>
                          <button 
                            onClick={() => handleUnban(pb.name)}
                            style={{ 
                              background: 'rgba(0, 184, 148, 0.1)', 
                              border: '1px solid #00B894', 
                              color: '#00B894', 
                              cursor: 'pointer',
                              padding: '4px 8px',
                              borderRadius: '2px',
                              fontSize: '0.6rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            <ShieldCheck size={12} /> RESTORE
                          </button>
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#555' }}>NO PROHIBITED BRANDS IN RECORDS</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <p style={{ color: '#555' }}>No telemetry data available.</p>
        )}
      </div>
    </div>
  );
};

const StatusCard = ({ icon: Icon, label, value, color, animated }) => (
  <div style={{ 
    backgroundColor: 'rgba(255,255,255,0.02)', 
    padding: '20px', 
    borderRadius: '4px', 
    border: `1px solid ${color}44`,
    textAlign: 'center'
  }}>
    <Icon size={24} color={color} className={animated ? 'animate-spin' : ''} style={{ marginBottom: '12px' }} />
    <div style={{ fontSize: '0.7rem', color: '#888', letterSpacing: '1px', marginBottom: '5px' }}>{label}</div>
    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: color }}>{value}</div>
  </div>
);

const getStatusColor = (status) => {
  switch (status) {
    case 'pending': return '#888';
    case 'scanning':
    case 'processing': return '#00CEC9';
    case 'downloaded':
    case 'completed': return '#00B894';
    case 'failed': return '#D63031';
    default: return '#FFF';
  }
};

export default MissionStatusPage;
